import type { BrowserProvider } from '@coti-io/coti-ethers';
import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  NFTCard,
  NFTCardImage,
  NFTCornerIcon,
  NFTDetailsContainer,
  NFTDetailsImageContainer,
  NFTDetailsContent,
  NFTDetailsRow,
  NFTDetailsLabel,
  NFTDetailsValue,
  NFTDetailsDisclaimer,
  SendButton,
  AddressBadge,
  AddressCopyButton,
  TokenDetailsLink,
  IconButton,
  MenuDropdown,
  MenuItem,
  HeaderBar,
} from './styles';
import { HeaderBarSlotLeft, HeaderBarSlotRight } from './styles/transfer';
import ArrowBack from '../../assets/arrow-back.svg';
import DefaultNFTImage from '../../assets/images/default_nft.png';
import CopyIcon from '../../assets/copy.svg';
import CopySuccessIcon from '../../assets/copy-success.svg';
import VerticalMenuIcon from '../../assets/icons/vertical-menu.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import { useChainId } from 'wagmi';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useDropdown } from '../../hooks/useDropdown';
import { useImportedTokens } from '../../hooks/useImportedTokens';
import { useTokenOperations } from '../../hooks/useTokenOperations';
import { getNetworkConfig } from '../../config/networks';
import type { ImportedToken } from '../../types/token';
import {
  parseNFTAddress,
  formatAddressForDisplay,
} from '../../utils/tokenValidation';

// Alternative IPFS gateways for fallback
const IPFS_GATEWAYS = [
  'https://cloudflare-ipfs.com/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://dweb.link/ipfs/',
];

// Extract CID from IPFS gateway URL (supports both path and subdomain formats)
const extractCIDFromUrl = (url: string): string | null => {
  try {
    // Path-based format: https://ipfs.io/ipfs/Qm...
    const pathMatch = url.match(/\/ipfs\/([^/?#]+)/);
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }

    // Subdomain format: https://Qm....ipfs.dweb.link/
    const subdomainMatch = url.match(/^https?:\/\/([a-z0-9]+)\.ipfs\./i);
    if (subdomainMatch?.[1]) {
      return subdomainMatch[1];
    }

    return null;
  } catch {
    return null;
  }
};

type NFTDetailModalProps = {
  nft: ImportedToken | null;
  open: boolean;
  onClose: () => void;
  setActiveTab: React.Dispatch<React.SetStateAction<'tokens' | 'nfts'>>;
  setSelectedNFT: React.Dispatch<React.SetStateAction<ImportedToken | null>>;
  provider?: BrowserProvider;
  onNFTRemoved?: () => void;
  onSendClick?: (nft: ImportedToken) => void;
  imageUrl?: string | undefined;
  aesKey?: string | null | undefined;
};

const NFTDetails: React.FC<NFTDetailModalProps> = ({
  nft,
  open,
  onClose,
  setActiveTab,
  setSelectedNFT,
  provider,
  onNFTRemoved,
  onSendClick,
  imageUrl,
  aesKey,
}) => {
  const chainId = useChainId();
  const networkConfig = getNetworkConfig(chainId);
  const { copyToClipboard, copied } = useCopyToClipboard();
  const { removeToken } = useImportedTokens();
  const menuDropdown = useDropdown();
  const [erc1155Balance, setErc1155Balance] = useState<string>('1');
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [displayImage, setDisplayImage] = useState<string>(
    imageUrl && imageUrl.trim() !== '' ? imageUrl : DefaultNFTImage,
  );
  const [fallbackIndex, setFallbackIndex] = useState<number>(0);
  const originalImageRef = useRef<string | undefined>(imageUrl);
  const balanceCache = useRef<Map<string, string>>(new Map());
  const fetchingRef = useRef<Set<string>>(new Set());

  const tokenOps = provider ? useTokenOperations(provider) : null;

  if (!open || !nft) {
    return null;
  }

  const { contractAddress: contract, tokenId } = useMemo(
    () => parseNFTAddress(nft.address),
    [nft.address],
  );

  useEffect(() => {
    originalImageRef.current = imageUrl;
    const finalImage =
      imageUrl && imageUrl.trim() !== '' ? imageUrl : DefaultNFTImage;
    setDisplayImage(finalImage);
    setFallbackIndex(0);
  }, [imageUrl]);

  const handleImageError = useCallback(() => {
    if (displayImage === DefaultNFTImage) {
      return;
    }

    const originalUrl = originalImageRef.current;
    if (!originalUrl) {
      setDisplayImage(DefaultNFTImage);
      return;
    }

    // Try to extract CID and use alternative gateways
    const cid = extractCIDFromUrl(originalUrl);

    if (!cid) {
      setDisplayImage(DefaultNFTImage);
      return;
    }

    // Generate all possible gateway URLs (both path and subdomain formats)
    const allGateways = [
      ...IPFS_GATEWAYS.map((gateway) => `${gateway}${cid}`),
      `https://${cid}.ipfs.dweb.link/`,
      `https://${cid}.ipfs.cf-ipfs.com/`,
    ];

    if (fallbackIndex < allGateways.length) {
      const nextUrl = allGateways[fallbackIndex];
      if (nextUrl) {
        setDisplayImage(nextUrl);
        setFallbackIndex((prev) => prev + 1);
      }
    } else {
      // No more fallbacks, use default image
      setDisplayImage(DefaultNFTImage);
    }
  }, [displayImage, fallbackIndex]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (
        nft?.type === 'ERC1155' &&
        contract &&
        tokenId &&
        tokenOps &&
        provider
      ) {
        const cacheKey = `${contract}-${tokenId}`;
        if (balanceCache.current.has(cacheKey)) {
          setErc1155Balance(balanceCache.current.get(cacheKey)!);
          return;
        }
        if (fetchingRef.current.has(cacheKey)) {
          return;
        }
        fetchingRef.current.add(cacheKey);
        setIsLoadingBalance(true);
        try {
          const signer = await provider.getSigner();
          const userAddress = await signer.getAddress();
          const balance = await tokenOps.getERC1155Balance(
            contract,
            userAddress,
            tokenId,
          );
          balanceCache.current.set(cacheKey, balance);
          setErc1155Balance(balance);
        } catch (error) {
          setErc1155Balance('0');
        } finally {
          setIsLoadingBalance(false);
          fetchingRef.current.delete(cacheKey);
        }
      }
    };

    fetchBalance();
  }, [nft?.type, contract, tokenId, tokenOps, provider]);

  useEffect(() => {
    if (!nft || !tokenOps || !provider) {
      return;
    }
    if (imageUrl && imageUrl.trim() !== '') {
      return;
    }
    if (!contract || !tokenId) {
      return;
    }

    let cancelled = false;

    const loadMetadataImage = async () => {
      try {
        const metadata = await tokenOps.getNFTMetadata({
          tokenAddress: contract,
          tokenId,
          tokenType: nft.type,
          ...(aesKey && { aesKey }),
        });
        if (!cancelled && metadata?.image) {
          setDisplayImage(metadata.image);
        }
      } catch (error) {
        if (!cancelled) {
          void error;
        }
      }
    };

    void loadMetadataImage();

    return () => {
      cancelled = true;
    };
  }, [nft, tokenOps, provider, contract, tokenId, imageUrl, aesKey]);

  const shortAddress = contract ? formatAddressForDisplay(contract) : '';

  const handleCopy = useCallback(
    (text: string) => {
      copyToClipboard(text);
    },
    [copyToClipboard],
  );

  const handleRemoveToken = useCallback(() => {
    if (nft) {
      removeToken(nft.address);
      onClose();
      onNFTRemoved?.();
    }
    menuDropdown.close();
  }, [nft, removeToken, onClose, onNFTRemoved, menuDropdown]);

  return (
    <NFTDetailsContainer>
      <HeaderBar>
        <HeaderBarSlotLeft>
          <IconButton onClick={onClose} type="button" aria-label="Go back">
            <ArrowBack />
          </IconButton>
        </HeaderBarSlotLeft>
        <HeaderBarSlotRight style={{ position: 'relative' }}>
          <IconButton
            onClick={menuDropdown.toggle}
            selected={menuDropdown.isOpen}
            type="button"
            aria-label="Menu"
          >
            <VerticalMenuIcon />
          </IconButton>

          {menuDropdown.isOpen && nft?.symbol !== 'COTI' && (
            <MenuDropdown ref={menuDropdown.ref}>
              <MenuItem onClick={handleRemoveToken} type="button">
                <TrashIcon />
                Hide {nft.symbol}
              </MenuItem>
            </MenuDropdown>
          )}
        </HeaderBarSlotRight>
      </HeaderBar>
      <NFTDetailsImageContainer>
        <NFTCard style={{ width: 140, height: 140, borderRadius: 16 }}>
          <NFTCardImage
            src={displayImage}
            alt="NFT"
            loading="lazy"
            onError={handleImageError}
          />
          <NFTCornerIcon>C</NFTCornerIcon>
        </NFTCard>
      </NFTDetailsImageContainer>
      <NFTDetailsContent>
        <NFTDetailsRow>
          <NFTDetailsLabel>Contract address</NFTDetailsLabel>
          <AddressBadge>
            <TokenDetailsLink
              href={`${networkConfig.explorerUrl}/address/${contract}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shortAddress}
            </TokenDetailsLink>
            <AddressCopyButton onClick={() => handleCopy(contract)}>
              {copied ? <CopySuccessIcon /> : <CopyIcon />}
            </AddressCopyButton>
          </AddressBadge>
        </NFTDetailsRow>
        <NFTDetailsRow>
          <NFTDetailsLabel>Token ID</NFTDetailsLabel>
          <NFTDetailsValue>{tokenId}</NFTDetailsValue>
        </NFTDetailsRow>
        {nft.type === 'ERC1155' && (
          <NFTDetailsRow>
            <NFTDetailsLabel>Balance</NFTDetailsLabel>
            <NFTDetailsValue>
              {isLoadingBalance ? '...' : `${erc1155Balance}`}
            </NFTDetailsValue>
          </NFTDetailsRow>
        )}
        <NFTDetailsRow>
          <NFTDetailsLabel>Token Standard</NFTDetailsLabel>
          <NFTDetailsValue>{nft.type || 'ERC721'}</NFTDetailsValue>
        </NFTDetailsRow>
        <NFTDetailsDisclaimer>
          Disclaimer: MetaMask pulls the media file from the source url. This
          url sometimes gets changed by the marketplace on which the NFT was
          minted.
        </NFTDetailsDisclaimer>
        <SendButton onClick={() => nft && onSendClick && onSendClick(nft)}>
          Send
        </SendButton>
      </NFTDetailsContent>
    </NFTDetailsContainer>
  );
};

export default NFTDetails;
